import React from "react";
import { Button, Column, Icon, Row, Sheet, Text, Loader } from "@amzn/meridian";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import { NON_COMPLIANT } from "src/constants/constants";

interface InspectionResultSheetProps {
  open: boolean;
  isLoading: boolean;
  aiResult: string;
  aiReasoning?: string;
  streamingMode: boolean;
  streamingDevMode: boolean;
  streamingMetrics: {
    imagesSent: number;
    averageImageSize: number;
    p50Latency: number;
    p90Latency: number;
  };
  onClose: () => void;
  onOverride: () => void;
  onAccept: () => void;
  onSkip: () => void;
}

/**
 * Sheet component to display AI analysis results
 * Shows loading state, results, and action buttons
 */
const InspectionResultSheet: React.FC<InspectionResultSheetProps> = ({
  open,
  isLoading,
  aiResult,
  aiReasoning,
  streamingMode,
  streamingDevMode,
  streamingMetrics,
  onClose,
  onOverride,
  onAccept,
  onSkip,
}) => {
  return (
    <Sheet
      className="ai-answer-dialogue-sheet"
      type="overlay"
      side="bottom"
      open={open}
      onClose={onClose}
    >
      <div style={{ borderRadius: "21px" }}>
        {isLoading || !aiResult ? (
          // Loading state - show spinner with skip option
          <Column spacing="300" alignmentHorizontal="center">
            <Row spacing="200" alignmentVertical="center" alignmentHorizontal="center">
              <Loader size="medium" />
              <Text type="h400">Analyzing photo...</Text>
            </Row>
            <Text type="b100" color="secondary">
              This may take a few seconds. You can skip to continue inspection.
            </Text>
          </Column>
        ) : (
          <Column>
            <Row alignmentHorizontal="justify">
              <Column spacing="100">
                <Text>AI Identified status</Text>
                <Text
                  color={aiResult === NON_COMPLIANT ? "error" : "success"}
                  type="h400"
                >
                  {aiResult}
                </Text>
              </Column>
              <Button onClick={onClose} type="icon">
                <Icon tokens={closeSmallTokens} />
              </Button>
            </Row>

            {/* AI Reasoning */}
            {aiReasoning && (
              <Column spacing="100" spacingInset="300 none none">
                <Text type="b200">AI Reasoning:</Text>
                <Text type="b100" color="secondary">
                  {aiReasoning}
                </Text>
              </Column>
            )}

            {/* Session Statistics - only show in dev mode */}
            {streamingDevMode && streamingMode && streamingMetrics.imagesSent > 0 && (
              <Column spacing="100" spacingInset="300 none none">
                <Text type="b200">Session Statistics</Text>
                <Row alignmentHorizontal="justify">
                  <Text type="b100">Images Sent:</Text>
                  <Text type="b100">{streamingMetrics.imagesSent}</Text>
                </Row>
                <Row alignmentHorizontal="justify">
                  <Text type="b100">Avg Image Size:</Text>
                  <Text type="b100">{streamingMetrics.averageImageSize} KB</Text>
                </Row>
                <Row alignmentHorizontal="justify">
                  <Text type="b100">P50 Latency:</Text>
                  <Text type="b100">{streamingMetrics.p50Latency} ms</Text>
                </Row>
                <Row alignmentHorizontal="justify">
                  <Text type="b100">P90 Latency:</Text>
                  <Text type="b100">{streamingMetrics.p90Latency} ms</Text>
                </Row>
              </Column>
            )}
          </Column>
        )}

        <Row
          alignmentHorizontal="justify"
          widths={"fill"}
          spacingInset={"400 none none"}
        >
          {isLoading || !aiResult ? (
            <Button onClick={onSkip} type="primary" size="large">
              Skip - Continue Inspection
            </Button>
          ) : (
            <>
              <Button onClick={onOverride} type="tertiary">
                Override
              </Button>
              <Button onClick={onAccept} type="primary">
                Accept AI Answer
              </Button>
            </>
          )}
        </Row>
      </div>
    </Sheet>
  );
};

export default InspectionResultSheet;
